import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MatTableDataSource } from '@angular/material/table';
import { getHistoryChangedFields } from 'src/app/_helpers/history-changed-fields.helper';
import { SEG, SegRecordHistory } from 'src/app/models/seg.model';
import { SegOrgLevel, SegOrgLevelRow } from 'src/app/models/segOrgLevel.model';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import {
  SegOrgLevelBulkPatchResponse,
  SegOrgLevelBulkPatchRow,
  SegOrgLevelPatchErrorCode,
  SegOrgLevelService,
} from 'src/app/services/seg-org-level.service';
import { SegService } from 'src/app/services/seg.service';
import { ShiftService } from 'src/app/services/shift.service';
import { ToastService } from 'src/app/services/toast.service';
import { FLYOUT_DATA } from 'src/app/shared/flyout/flyout-container/flyout-data.token';
import { OrgTreeSelectComponent } from 'src/app/shared/org-tree-select/org-tree-select.component';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { buildHistoryParams, getHistoryDateTimeDisplay, loadPopupHistory } from 'src/app/_helpers/history-popup.helper';
import { catchError, forkJoin, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { GridPermissions, gridToPermissionFlags } from 'src/app/shared/grid/core-grid/gridPermissions.enum';
import { PermissionService } from 'src/app/services/permission.service';
import { hasSegOrgLevelPermission } from 'src/app/_helpers/seg-org-level-permission.helper';
import { getEnumLookup } from 'src/app/_helpers/enum-helper';
import { getDescendantOrSelfIds, toOrganizationTree } from 'src/app/_helpers/org-tree-helper';
import { ShiftType } from 'src/app/enums/shiftTypeValue.enum';
import { Lookup } from 'src/app/models/lookup.model';
import { Shift } from 'src/app/models/shift.model';
import { trackById } from 'src/app/shared/utils/track-by.util';
import { TranslateService } from '@ngx-translate/core';
import {
  EditableNumberField,
  NUMBER_FIELD_BOUNDS,
  NUMBER_FIELD_LABELS,
  isNumberFieldOutOfRange,
} from './seg-number-field-bounds';

type PendingField = EditableNumberField | 'shiftId';

export interface SegHeaderField {
  label: string;
  value: string | null;
  fullWidth?: boolean;
  translateValue?: boolean;
}

const ARCHIVE_TYPE_MERGED = 'Merged';
const SITE_COUNT_LABEL = 'Site Count';
const ACTIONS_COLUMN = 'actions';
const HISTORY_COLUMN = 'history';
const SEG_HISTORY_TAB_INDEX = 1;
const ORG_LEVEL_HISTORY_TAB_INDEX = 2;

interface OrgLevelRemovalResult {
  organizationLevelName?: string;
  success: boolean;
}

@Component({
  standalone: false,
  selector: 'app-seg-details-popup',
  templateUrl: './seg-details-popup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './seg-details-popup.component.scss'
})

export class SegDetailsPopupComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(OrgTreeSelectComponent) orgPicker?: OrgTreeSelectComponent;

  canEdit = false;
  canRemoveOrgLevel = false;

  readonly shiftPatternOptions: Lookup[] = getEnumLookup(ShiftType).map(
    (option) => ({ id: option.id, name: option.name.replace('Shift_', '') }),
  );

  segDetails!: SEG;
  headerFields: SegHeaderField[] = [];
  readonly trackByHeaderLabel = (_: number, field: SegHeaderField) => field.label;
  displayedColumns: string[] = [];
  private orgLevelColumns: string[] = [];
  dataSource = new MatTableDataSource<SegOrgLevelRow>([]);
  historyDisplayedColumns: string[] = [];
  historyDataSource = new MatTableDataSource<SegRecordHistory>([]);
  isLoading = false;
  isHistoryLoading = false;
  selectedTabIndex = 0;
  orgLevelHistoryScopeId: string | null = null;

  isEditing = false;
  isSaving = false;
  readonly trackByLookupId = trackById;
  shifts: Lookup[] = [];
  orgPickerNodes: OrganizationLevel[] = [];
  disabledIds: string[] = [];

  private referenceDataLoaded = false;
  private isReferenceLoading = false;
  private shiftsLoaded = false;
  private parentOrgLevelId: string | null = null;
  private readonly shiftPatternLabelById = new Map<number, string>(
    this.shiftPatternOptions.map((option) => [Number(option.id), option.name]),
  );
  private shiftById = new Map<string, string>();
  private shiftDataById = new Map<string, Shift>();
  private orgNameById = new Map<string, string>();
  private originalRows: SegOrgLevelRow[] = [];
  private originalById = new Map<string, SegOrgLevelRow>();
  private readonly destroy$ = new Subject<void>();
  private historyLoaded = false;
  private associationsLoaded = false;

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  constructor(
    @Inject(FLYOUT_DATA) public data: string,
    private segService: SegService,
    private segOrgLevelService: SegOrgLevelService,
    private organizationLevelService: OrganizationLevelService,
    private shiftService: ShiftService,
    private dateFormatService: DateFormatService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private permissionService: PermissionService,
  ) { }

  ngOnInit(): void {
    this.canEdit = this.permissionService.hasPermissionFlag(
      RolePermissionObjects.SEG,
      gridToPermissionFlags[GridPermissions.Update],
    ) || hasSegOrgLevelPermission(this.permissionService, GridPermissions.Update);

    // A removal is authorized on its own Delete flag (PermissionCheckInterceptor runs before
    // SoftDeleteIntercept), so gate the per-row remove action separately from Edit.
    this.canRemoveOrgLevel = this.permissionService.hasPermissionFlag(
      RolePermissionObjects.SEG,
      gridToPermissionFlags[GridPermissions.Delete],
    ) || hasSegOrgLevelPermission(this.permissionService, GridPermissions.Delete);

    if (this.data) {
      this.getSegDetails();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getSegDetails() {
    this.isLoading = true;
    this.segService.getById(this.data).subscribe({
      next: (response) => {
        this.segDetails = response;
        this.buildHeaderFields(response);
        this.setColumns();
        // getParentId owns the loading lifecycle from here (it, and the
        // getSegOrgLabels call it triggers, clear isLoading once associations arrive).
        this.getParentId();
      },
      error: (error) => {
        this.toastService.showError(error);
        this.isLoading = false;
      }
    });
  }
  private buildHeaderFields(seg: SEG): void {
    const fields: SegHeaderField[] = [
      { label: 'SEG Name', value: this.nullIfEmpty(seg.name) },
      { label: 'Code', value: this.nullIfEmpty(seg.code) },
      { label: 'Organization Level', value: this.nullIfEmpty(seg.organizationLevelName) },
      // Derived from the loaded associations (the table below), not seg.siteCount,
      // so the header always equals the rows on screen (blank until they arrive).
      { label: SITE_COUNT_LABEL, value: this.getSiteCountValue() },
      { label: 'Updated Date', value: seg.dateUpdated ? getHistoryDateTimeDisplay(seg.dateUpdated, this.dateFormatService) : null },
      { label: 'Updated By', value: this.nullIfEmpty(seg.updatedByUserName) },
      { label: 'Description', value: this.nullIfEmpty(seg.description), fullWidth: true },
    ];

    if (seg.archiveType) {
      fields.push({ label: 'Archive Type', value: seg.archiveType, translateValue: true });
      if (seg.archiveType === ARCHIVE_TYPE_MERGED) {
        fields.push({ label: 'Merged Into', value: this.nullIfEmpty(seg.mergedIntoName) });
      }
    }

    this.headerFields = fields;
  }

  private nullIfEmpty(value: string | null | undefined): string | null {
    return value == null || value === '' ? null : value;
  }

  private getSiteCountValue(): string | null {
    if (!this.associationsLoaded) {
      return null;
    }
    return String(this.dataSource.data.filter((row) => !row.isRemoved).length);
  }

  // Re-sync the header count with the table when the rows change, without a reopen.
  private refreshSiteCountHeader(): void {
    const field = this.headerFields.find((f) => f.label === SITE_COUNT_LABEL);
    if (field) {
      field.value = this.getSiteCountValue();
    }
  }

  setColumns() {
    this.orgLevelColumns = [
      nameOf<SegOrgLevel>(x => x.OrgLevelName),
      nameOf<SegOrgLevel>(x => x.shiftId),
      nameOf<SegOrgLevel>(x => x.shiftLengthHours),
      nameOf<SegOrgLevel>(x => x.avgWeeklyHours),
      nameOf<SegOrgLevel>(x => x.shiftType),
      nameOf<SegOrgLevel>(x => x.numberOfWorkers),
      nameOf<SegOrgLevel>(x => x.numberOfContractors),
    ];
    this.updateDisplayedColumns();
    this.historyDisplayedColumns = [
      nameOf<SegRecordHistory>(x => x.name),
      nameOf<SegRecordHistory>(x => x.organizationLevelName),
      nameOf<SegRecordHistory>(x => x.code),
      nameOf<SegRecordHistory>(x => x.reason),
      nameOf<SegRecordHistory>(x => x.dateUpdated),
      nameOf<SegRecordHistory>(x => x.updatedByUserName)
    ];
  }

  getHistory(): void {
    this.isHistoryLoading = true;
    const params = buildHistoryParams('segId', this.data);

    loadPopupHistory(
      this.segService.getHistory(params),
      this.destroy$,
      (history) => this.mapHistoryRecord(history),
      (records) => {
        this.historyDataSource.data = records;
        this.historyLoaded = true;
        this.isHistoryLoading = false;
      },
      (error) => {
        this.toastService.showError(error);
        this.historyDataSource.data = [];
        this.historyLoaded = false;
        this.isHistoryLoading = false;
      },
    );
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.selectedTabIndex = event.index;
    if (event.index !== ORG_LEVEL_HISTORY_TAB_INDEX) {
      this.orgLevelHistoryScopeId = null;
    }
    if (event.index === SEG_HISTORY_TAB_INDEX && !this.historyLoaded) {
      this.getHistory();
    }
  }

  openOrgLevelHistory(row: SegOrgLevelRow): void {
    if (!row.id) {
      return;
    }
    this.orgLevelHistoryScopeId = row.id;
    this.selectedTabIndex = ORG_LEVEL_HISTORY_TAB_INDEX;
  }

  private mapHistoryRecord(history: SegRecordHistory): SegRecordHistory {
    return {
      ...history,
      changedFieldSet: new Set(getHistoryChangedFields(history)),
      dateUpdatedDisplay: getHistoryDateTimeDisplay(history.dateUpdated, this.dateFormatService),
    };
  }

  getParentId() {
    this.isLoading = true;
    this.organizationLevelService.getParentSite(this.segDetails.organizationLevelId).subscribe({
      next: (response) => {
        const parentId = response?.parentId ?? null;
        const orgLevelType = response?.orgLevelType;
        if (parentId == null) {
          // No parent scope available (SEG sits at a root-level site, or no
          // response) — fall back to the unscoped, segId-only association query
          // so the org-level table still loads. Matches pre-existing behaviour.
          this.getSegOrgLabels(this.data);
          return;
        }
        this.parentOrgLevelId = parentId;
        this.getSegOrgLabels(this.data, parentId, orgLevelType);
      },
      error: (error) => {
        this.toastService.showError(error);
        this.isLoading = false;
      }
    });
  }

  getSegOrgLabels(segId: string, parentOrgLevelId?: string, orgLevelType?: number): void {
    this.isLoading = true;
    // Load the shift reference data alongside the associations so the Shift
    // column read view can resolve a linked shift's name (SegOrgLevelDto only
    // returns shiftId — the name is resolved client-side).
    forkJoin({
      associations: this.segOrgLevelService.getAssociations(segId, parentOrgLevelId, orgLevelType),
      shifts: this.ensureShifts$(),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ associations }) => {
        this.dataSource.data = associations.map((row) => this.toRow(row));
        this.associationsLoaded = true;
        this.refreshSiteCountHeader();
        this.isLoading = false;
      },
      error: (error) => {
        this.toastService.showError(error);
        this.isLoading = false;
      }
    });
  }

  enterEditMode(): void {
    this.loadEditReferenceData();
    this.originalRows = this.dataSource.data.map((row) => this.cloneRow(row));
    this.originalById = new Map(this.originalRows.map((row) => [row.organizationLevelId, row]));
    this.refreshDisabledIds();
    this.isEditing = true;
    this.updateDisplayedColumns();
  }

  cancelEdit(): void {
    this.dataSource.data = this.originalRows.map((row) => this.cloneRow(row));
    this.orgPicker?.reset();
    this.refreshDisabledIds();
    this.refreshSiteCountHeader();
    this.isEditing = false;
    this.updateDisplayedColumns();
  }

  // A new (unsaved) row is dropped outright; an existing association is marked as a staged
  // removal (struck through) and only deleted on Save, so Cancel fully reverts it via originalRows.
  toggleRemoveRow(row: SegOrgLevelRow): void {
    if (row.isNew) {
      this.dataSource.data = this.dataSource.data.filter((item) => item !== row);
    } else {
      row.isRemoved = !row.isRemoved;
      this.dataSource.data = [...this.dataSource.data];
    }
    this.refreshDisabledIds();
    this.refreshSiteCountHeader();
  }

  private updateDisplayedColumns(): void {
    if (!this.isEditing) {
      this.displayedColumns = [...this.orgLevelColumns, HISTORY_COLUMN];
      return;
    }
    this.displayedColumns = this.canRemoveOrgLevel
      ? [...this.orgLevelColumns, ACTIONS_COLUMN]
      : [...this.orgLevelColumns];
  }

  saveEdits(): void {
    if (this.isSaving) {
      return;
    }

    const validationErrors = this.getRowValidationErrors();
    if (validationErrors.length) {
      validationErrors.forEach((error) =>
        this.toastService.displayErrorMessage(
          '{{field}} must be a number between {{min}} and {{max}}.',
          true,
          error,
        ),
      );
      return;
    }

    const patchRows = this.buildPatchRows();
    const newOrgLevelIds = this.dataSource.data
      .filter((row) => row.isNew && !row.isRemoved)
      .map((row) => row.organizationLevelId);
    // Only existing (persisted) associations need a server-side delete; a removed new row
    // was never saved, so toggleRemoveRow already dropped it locally. Gate on
    // canRemoveOrgLevel so a user without Delete cannot trigger a DELETE via tampered state.
    const removedRows = this.canRemoveOrgLevel
      ? this.dataSource.data.filter((row) => row.isRemoved && !row.isNew)
      : [];

    if (!patchRows.length && !newOrgLevelIds.length && !removedRows.length) {
      this.isEditing = false;
      return;
    }

    this.isSaving = true;
    const add$: Observable<unknown> = newOrgLevelIds.length
      ? this.segOrgLevelService.bulkAddSegOrgLevels({ segIds: [this.segDetails.id], orgLevelIds: newOrgLevelIds })
      : of(null);

    add$.pipe(
      switchMap(() => (patchRows.length
        ? this.segOrgLevelService.bulkPatch({ rows: patchRows })
        : of<SegOrgLevelBulkPatchResponse | null>(null))),
      switchMap((patchResponse) => this.commitRemovals$(removedRows).pipe(
        map((removalResults) => ({ patchResponse, removalResults })),
      )),
      takeUntil(this.destroy$),
    ).subscribe({
      next: ({ patchResponse, removalResults }) => {
        this.isSaving = false;
        this.reportSaveOutcome(patchResponse, removalResults);
        this.isEditing = false;
        this.getSegDetails();
      },
      error: (error) => {
        this.isSaving = false;
        this.toastService.showError(error);
      },
    });
  }

  // There is no bulk-delete endpoint, so each staged removal goes through the single
  // DELETE /SegOrgLevel/Delete. Per-row failures are captured (not thrown) so one bad row
  // never aborts the others, and the reload afterwards reflects exactly what persisted.
  private commitRemovals$(removedRows: SegOrgLevelRow[]): Observable<OrgLevelRemovalResult[]> {
    if (!removedRows.length) {
      return of([]);
    }
    return forkJoin(
      removedRows.map((row) =>
        this.segOrgLevelService.deleteById(this.segDetails.id, row.organizationLevelId).pipe(
          map(() => ({ organizationLevelName: row.organizationLevelName, success: true })),
          catchError(() => of({ organizationLevelName: row.organizationLevelName, success: false })),
        ),
      ),
    );
  }

  private reportSaveOutcome(
    patchResponse: SegOrgLevelBulkPatchResponse | null,
    removalResults: OrgLevelRemovalResult[],
  ): void {
    const removalFailures = removalResults.filter((result) => !result.success);
    const patchFailed = this.hasPatchFailure(patchResponse);

    if (!patchFailed && !removalFailures.length) {
      this.toastService.showSuccess('Changes saved', true);
      return;
    }

    const serverErrors = this.getPatchErrorMessages(patchResponse);
    serverErrors.forEach((message) => this.toastService.displayErrorMessage(message));
    removalFailures.forEach((failure) =>
      this.toastService.displayErrorMessage(
        'Could not remove {{name}} from the site.',
        true,
        { name: failure.organizationLevelName ?? '' },
      ),
    );
    if (patchFailed && !serverErrors.length) {
      this.toastService.showWarning('Some changes could not be saved.', true);
    }
  }

  onAddOrgLevel(orgLevelId: string | null): void {
    this.orgPicker?.reset();
    if (!orgLevelId || this.dataSource.data.some((row) => row.organizationLevelId === orgLevelId)) {
      return;
    }
    const newRow: SegOrgLevelRow = {
      segId: this.segDetails.id,
      organizationLevelId: orgLevelId,
      organizationLevelName: this.orgNameById.get(orgLevelId),
      shiftId: null,
      shiftName: undefined,
      numberOfWorkers: null,
      numberOfContractors: null,
      shiftLengthHours: null,
      avgWeeklyHours: null,
      shiftType: null,
      isNew: true,
    };
    this.dataSource.data = [...this.dataSource.data, newRow];
    this.refreshDisabledIds();
    this.refreshSiteCountHeader();
  }

  onShiftTypeChange(row: SegOrgLevelRow): void {
    row.shiftTypeLabel = this.shiftTypeLabel(row.shiftType);
  }

  isFieldOutOfRange(row: SegOrgLevelRow, field: EditableNumberField): boolean {
    return isNumberFieldOutOfRange(row[field], field);
  }

  // Whether an editable field on a row has unsaved (pending-save) changes, so the
  // template can visually flag it while in edit mode. A brand-new row is entirely
  // pending; otherwise a field is pending when its current value differs from the
  // originally loaded value (snapshotted on enterEditMode). Mirrors the diff logic
  // in buildPatchRow so what is flagged is exactly what will be persisted on Save.
  isFieldPending(row: SegOrgLevelRow, field: PendingField): boolean {
    if (!this.isEditing) {
      return false;
    }
    if (row.isNew) {
      return true;
    }
    const original = this.originalById.get(row.organizationLevelId);
    if (!original) {
      return false;
    }
    if (field === 'shiftId') {
      return (row.shiftId ?? null) !== (original.shiftId ?? null);
    }
    return this.normalizeNumber(row[field]) !== this.normalizeNumber(original[field]);
  }

  // When a shift is linked, the shift owns Shift Length / Avg Weekly Hours /
  // Shift Pattern. Fetch the selected shift's full details and populate those
  // fields (and the name label) immediately so the derived values display right
  // away in edit mode instead of only appearing after Save. Shift/GetAll returns
  // id+name only, so the full shift is fetched via getById (cached per shift).
  // Clearing the shift drops the name label and leaves the copied values editable
  // (matches buildPatchRow's clear-shift behaviour).
  onShiftChange(row: SegOrgLevelRow): void {
    const shiftId = row.shiftId != null ? String(row.shiftId) : null;
    if (!shiftId) {
      row.shiftName = undefined;
      row.shiftTypeLabel = this.shiftTypeLabel(row.shiftType);
      return;
    }
    const cached = this.shiftDataById.get(shiftId);
    if (cached) {
      this.applyShiftToRow(row, cached);
      return;
    }
    this.shiftService.getById(shiftId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (shift) => {
        if (!shift) {
          return;
        }
        this.shiftDataById.set(shiftId, shift);
        // Ignore a stale response if the row's selection changed while in flight.
        if (String(row.shiftId) === shiftId) {
          this.applyShiftToRow(row, shift);
        }
      },
      error: (error) => this.toastService.showError(error),
    });
  }

  private applyShiftToRow(row: SegOrgLevelRow, shift: Shift): void {
    row.shiftName = shift.name;
    row.shiftLengthHours = shift.shiftLengthHours ?? null;
    row.avgWeeklyHours = shift.averageWeeklyHours ?? null;
    row.shiftType = shift.shiftType ?? null;
    row.shiftTypeLabel = this.shiftTypeLabel(row.shiftType);
  }

  private loadEditReferenceData(): void {
    if (this.referenceDataLoaded || this.isReferenceLoading) {
      return;
    }
    this.isReferenceLoading = true;
    forkJoin({
      shifts: this.ensureShifts$(),
      orgLevels: this.organizationLevelService.getAll(),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ orgLevels }) => {
        this.buildOrgPicker(orgLevels);
        this.referenceDataLoaded = true;
        this.isReferenceLoading = false;
      },
      error: (error) => {
        this.isReferenceLoading = false;
        this.toastService.showError(error);
      },
    });
  }

  // Loads the shift list once and caches it (both the sorted dropdown list and
  // the id→name lookup used to label linked shifts in the read view). Subsequent
  // calls short-circuit so the read path and edit path share a single fetch.
  private ensureShifts$(): Observable<Lookup[]> {
    if (this.shiftsLoaded) {
      return of(this.shifts);
    }
    return this.shiftService.getAll().pipe(
      map((shifts) => {
        this.shifts = shifts
          .map((shift) => ({ id: String(shift.id), name: String(shift.name) }))
          .sort((left, right) => left.name.localeCompare(right.name));
        this.shiftById = new Map(this.shifts.map((shift) => [String(shift.id), shift.name]));
        this.shiftsLoaded = true;
        return this.shifts;
      }),
    );
  }

  private buildOrgPicker(orgLevels: OrganizationLevel[]): void {
    this.orgNameById = new Map(orgLevels.map((orgLevel) => [orgLevel.id, orgLevel.name]));
    const tree = toOrganizationTree(orgLevels);
    const siteNode = this.parentOrgLevelId ? this.findNode(tree, this.parentOrgLevelId) : null;
    this.orgPickerNodes = siteNode ? [siteNode] : tree;
  }

  private findNode(nodes: OrganizationLevel[], id: string): OrganizationLevel | null {
    for (const node of nodes) {
      if (node.id === id) {
        return node;
      }
      if (node.children?.length) {
        const found = this.findNode(node.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  private refreshDisabledIds(): void {
    this.disabledIds = this.dataSource.data.map((row) => row.organizationLevelId);
  }

  private buildPatchRows(): SegOrgLevelBulkPatchRow[] {
    const rows: SegOrgLevelBulkPatchRow[] = [];
    for (const row of this.dataSource.data) {
      if (row.isRemoved) {
        continue;
      }
      const original = row.isNew ? undefined : this.originalById.get(row.organizationLevelId);
      const payload = this.buildPatchRow(row, original);
      if (payload) {
        rows.push(payload);
      }
    }
    return rows;
  }

  private buildPatchRow(row: SegOrgLevelRow, original?: SegOrgLevelRow): SegOrgLevelBulkPatchRow | null {
    const payload: SegOrgLevelBulkPatchRow = { segId: this.segDetails.id, orgLevelId: row.organizationLevelId };
    const clearFields: string[] = [];
    let changed = false;

    const originalShiftId = original?.shiftId ?? null;
    const newShiftId = row.shiftId ?? null;
    if (newShiftId !== originalShiftId) {
      changed = true;
      if (newShiftId) {
        payload.shiftId = newShiftId;
      } else {
        clearFields.push('shiftId');
      }
    }

    changed = this.applyNumberField(payload, clearFields, 'numberOfWorkers', row.numberOfWorkers, original?.numberOfWorkers) || changed;
    changed = this.applyNumberField(payload, clearFields, 'numberOfContractors', row.numberOfContractors, original?.numberOfContractors) || changed;

    if (!newShiftId) {
      changed = this.applyNumberField(payload, clearFields, 'shiftLengthHours', row.shiftLengthHours, original?.shiftLengthHours) || changed;
      changed = this.applyNumberField(payload, clearFields, 'avgWeeklyHours', row.avgWeeklyHours, original?.avgWeeklyHours) || changed;
      changed = this.applyNumberField(payload, clearFields, 'shiftType', row.shiftType, original?.shiftType) || changed;
    }

    if (clearFields.length) {
      payload.clearFields = clearFields;
    }
    return changed ? payload : null;
  }

  private applyNumberField(
    payload: SegOrgLevelBulkPatchRow,
    clearFields: string[],
    field: EditableNumberField,
    newValue: number | null | undefined,
    originalValue: number | null | undefined,
  ): boolean {
    const normalizedNew = this.normalizeNumber(newValue);
    const normalizedOriginal = this.normalizeNumber(originalValue);
    if (normalizedNew === normalizedOriginal) {
      return false;
    }
    if (normalizedNew === null) {
      clearFields.push(field);
    } else {
      payload[field] = normalizedNew;
    }
    return true;
  }

  private getRowValidationErrors(): { field: string; min: number; max: number }[] {
    const invalidFields = new Map<EditableNumberField, { field: string; min: number; max: number }>();
    for (const row of this.dataSource.data) {
      (Object.keys(NUMBER_FIELD_BOUNDS) as EditableNumberField[]).forEach((field) => {
        const bounds = NUMBER_FIELD_BOUNDS[field];
        if (!bounds || invalidFields.has(field)) {
          return;
        }
        const value = this.normalizeNumber(row[field]);
        if (value === null) {
          return;
        }
        if (value < bounds.min || value > bounds.max) {
          invalidFields.set(field, {
            field: this.translateService.instant(NUMBER_FIELD_LABELS[field] ?? field),
            min: bounds.min,
            max: bounds.max,
          });
        }
      });
    }
    return Array.from(invalidFields.values());
  }

  private normalizeNumber(
    value: number | null | undefined,
  ): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return null;
    }
    // No clamping: out-of-range values are rejected in getRowValidationErrors, not coerced.
    return parsed;
  }

  private hasPatchFailure(response: SegOrgLevelBulkPatchResponse | null): boolean {
    return !!response?.results?.some((result) =>
      result.success === false ||
      (result.errorCode !== undefined && result.errorCode !== SegOrgLevelPatchErrorCode.None) ||
      !!result.error);
  }

  private getPatchErrorMessages(response: SegOrgLevelBulkPatchResponse | null): string[] {
    const messages = (response?.results ?? [])
      .filter((result) => result.success === false && !!result.error)
      .map((result) => result.error as string);
    return Array.from(new Set(messages));
  }

  private toRow(row: SegOrgLevel): SegOrgLevelRow {
    return {
      ...row,
      shiftName: row.shiftId != null ? this.shiftById.get(String(row.shiftId)) : undefined,
      shiftTypeLabel: this.shiftTypeLabel(row.shiftType),
    };
  }

  private cloneRow(row: SegOrgLevelRow): SegOrgLevelRow {
    return { ...row };
  }

  private shiftTypeLabel(value: number | null | undefined): string | undefined {
    return value == null ? undefined : this.shiftPatternLabelById.get(Number(value));
  }
}
